/**
 * Player Defaults — centralized player initialization and per-turn reset.
 *
 * Eliminates duplication between main.ts (bootstrap), reset.ts (round reset),
 * and turns.ts (turn advance). Each site calls the appropriate function.
 */

import type { Player, GameMode } from '../types';
import { PHYSICS, MOMENTUM, HOP, CLASSIC, PLAYER_COLORS, STOCKS } from '../constants';
import { getGroundY } from '../terrain/grid';
import { lookupTank } from '../tanks';
import { getSkinForTank } from '../skins/storage';

/**
 * Create a fully-initialized Player at a spawn position.
 * Used by main.ts (bootstrap) and reset.ts (round reset).
 */
export function createPlayerDefaults(
  spawnX: number,
  spawnY: number,
  tankId: string,
  playerIndex: number,
  _mode: GameMode,
): Player {
  return {
    x: spawnX,
    y: spawnY,
    vx: 0,
    vy: 0,
    angle: Math.PI / 2,
    power: 0,
    charging: false,
    chargeDir: 1 as const,
    slingshotCenter: 0,
    slingshotRamp: 0,
    wobbleOffset: 0,
    color: getSkinForTank(tankId).color ?? lookupTank(tankId).visual.bodyColor,
    damagePercent: 0,
    hp: CLASSIC.MAX_HP,
    pendingDeath: false,
    alive: true,
    lastHitBy: -1,
    rotation: 0,
    tumble: false,
    hitstunFrames: 0,
    launchArmor: 0,
    tumbleRecoveryFrame: 0,
    tumbleRecoveryMax: 0,
    momentum: MOMENTUM.MAX,
    weaponCharge: 0,
    chargeDrainedThisTurn: false,
    momentumTurnStart: MOMENTUM.MAX,
    momentumActivated: false,
    idleFrames: 0,
    moveDir: 0,
    prevMoveDir: 0,
    name: `P${playerIndex + 1}`,
    grappleKeyHeld: false,
    fireHeld: false,
    chargeGraceTimer: 0,
    chargeGracePower: 0,
    walkCatchupTimer: 0,
    walkDeltaX: 0,
    squash: 1.0,
    squashVel: 0,
    climbDustCooldown: 0,
    climbBurstFired: false,
    aimHeldFrames: 0,
    aimPrevAngle: 0,
    aimPrevDir: 0,
    aimPrevSpeed: 0,
    grounded: true,
    settled: true,
    settledX: spawnX,
    settledY: spawnY,
    diPendingVx: 0,
    diPendingVy: 0,
    diAngleShift: 0,
    kbStackHitCount: 0,
    kbStackFirstFrame: 0,
    tumbleTrailCooldown: 0,
    burstInputX: 0,
    burstInputY: 0,
    burstFlying: false,
    burstTrailCooldown: 0,
    burstBraced: false,
    specialUsed: false,
    specialHeld: false,
    swapHeld: false,
    grappleLaunched: false,
    controlRestored: false,
    landingTimer: 0,
    landingTimerMax: 0,
    defenseSubPhase: 'none' as const,
    brakeWindowFrames: 0,
    brakeUsed: false,
    jumpHeld: false,
    liftActive: false,
    liftFuel: HOP.MAX_LIFT_FUEL,
    liftTrailCooldown: 0,
    coyoteFrames: 0,
    jumpBufferFrames: 0,
    fastFallActive: false,
    fastFallHeld: false,
    fastFallTrailCooldown: 0,
    platformDropTimer: 0,
    onWireframe: false,
    actionState: { tag: 'settled' as const, settledX: spawnX, settledY: spawnY },
    tankId,
    selectedShotTier: 1,
    armedShotTier: null,
    armedDisrupted: false,
    terraformerMode: 'sapper' as const,
    selectedWeapon: null,
    pityCounter: 0,
    // Vortex portal interaction
    portalImmunityFrames: 0,
    portalJumpUsed: false,
    portalJumpFrame: -1,
    portalJumpTargetX: 0,
    portalJumpTargetY: 0,
    // Vortex Collapse
    collapseGrabbed: false,
    collapseFrame: 0,
    collapseTargetX: 0,
    collapseTargetY: 0,
    // Dash immunity (Duelist iaijutsu)
    dashImmune: 0,
    // Cupid mark
    cupidMarkTurns: 0,
    cupidMarkedBy: -1,
    // AI
    isAI: false,
    aiDifficulty: 0,
    // Roguelite dormancy
    dormant: false,
    // Stock system
    stocks: STOCKS.DEFAULT_COUNT,
    respawnTimer: 0,
    respawnInvulnFrames: 0,
  };
}

/**
 * Reset a player's per-turn state for a new turn.
 * Preserves: position, identity, damage%, tankId, terraformerMode, aimPrev* fields.
 * Momentum uses carry-over formula — caller handles that before calling this.
 *
 * Used by turns.ts (turn advance).
 */
export function resetPlayerForTurn(player: Player): void {
  player.power = 0;
  player.charging = false;
  player.chargeDir = 1;
  player.slingshotCenter = 0;
  player.slingshotRamp = 0;
  player.wobbleOffset = 0;
  player.chargeGraceTimer = 0;
  player.chargeGracePower = 0;
  player.momentumActivated = false;
  player.chargeDrainedThisTurn = false;
  player.idleFrames = 0;
  player.moveDir = 0;
  player.prevMoveDir = 0;
  player.tumble = false;
  player.hitstunFrames = 0;
  player.launchArmor = 0;
  player.walkCatchupTimer = 0;
  player.walkDeltaX = 0;
  player.aimHeldFrames = 0;
  player.fireHeld = false;
  player.grappleKeyHeld = false;
  player.specialUsed = false;
  player.specialHeld = false;
  player.swapHeld = false;
  player.jumpHeld = false;
  player.liftActive = false;
  player.liftFuel = HOP.MAX_LIFT_FUEL;
  player.liftTrailCooldown = 0;
  player.coyoteFrames = 0;
  player.jumpBufferFrames = 0;
  player.fastFallActive = false;
  player.fastFallHeld = false;
  player.fastFallTrailCooldown = 0;
  player.onWireframe = false;
  player.selectedShotTier = 1;
  // armedShotTier intentionally NOT reset here — persists across turn boundary.
  // turns.ts forces armed tier on turn start; damage.ts clears on knockback.
  player.armedDisrupted = false;
  player.selectedWeapon = null;
  // Vortex portal jump (once per turn)
  player.portalJumpUsed = false;
  player.portalJumpFrame = -1;
}

/**
 * Reset a player's full state for a new round (applied to existing player object).
 * Used by reset.ts which needs to mutate existing players (state.players array persists).
 */
export function resetPlayerForRound(player: Player, spawnX: number, spawnY: number): void {
  player.x = spawnX;
  player.y = spawnY;
  player.vx = 0;
  player.vy = 0;
  player.angle = Math.PI / 2;
  player.power = 0;
  player.charging = false;
  player.chargeDir = 1;
  player.slingshotCenter = 0;
  player.slingshotRamp = 0;
  player.wobbleOffset = 0;
  player.damagePercent = 0;
  player.hp = CLASSIC.MAX_HP;
  player.pendingDeath = false;
  player.alive = true;
  player.lastHitBy = -1;
  player.rotation = 0;
  player.tumble = false;
  player.hitstunFrames = 0;
  player.launchArmor = 0;
  player.tumbleRecoveryFrame = 0;
  player.tumbleRecoveryMax = 0;
  player.momentum = MOMENTUM.MAX;
  player.weaponCharge = 0;
  player.momentumTurnStart = MOMENTUM.MAX;
  player.momentumActivated = false;
  player.idleFrames = 0;
  player.moveDir = 0;
  player.prevMoveDir = 0;
  player.grappleKeyHeld = false;
  player.fireHeld = false;
  player.chargeGraceTimer = 0;
  player.chargeGracePower = 0;
  player.walkCatchupTimer = 0;
  player.walkDeltaX = 0;
  player.squash = 1.0;
  player.squashVel = 0;
  player.climbDustCooldown = 0;
  player.tumbleTrailCooldown = 0;
  player.climbBurstFired = false;
  player.aimHeldFrames = 0;
  player.grounded = true;
  player.settled = true;
  player.settledX = spawnX;
  player.settledY = spawnY;
  player.diPendingVx = 0;
  player.diPendingVy = 0;
  player.diAngleShift = 0;
  player.kbStackHitCount = 0;
  player.kbStackFirstFrame = 0;
  player.burstInputX = 0;
  player.burstInputY = 0;
  player.burstFlying = false;
  player.burstTrailCooldown = 0;
  player.burstBraced = false;
  player.specialUsed = false;
  player.specialHeld = false;
  player.swapHeld = false;
  player.grappleLaunched = false;
  player.controlRestored = false;
  player.landingTimer = 0;
  player.landingTimerMax = 0;
  player.defenseSubPhase = 'none';
  player.brakeWindowFrames = 0;
  player.brakeUsed = false;
  player.jumpHeld = false;
  player.liftActive = false;
  player.liftFuel = HOP.MAX_LIFT_FUEL;
  player.liftTrailCooldown = 0;
  player.coyoteFrames = 0;
  player.jumpBufferFrames = 0;
  player.fastFallActive = false;
  player.fastFallHeld = false;
  player.fastFallTrailCooldown = 0;
  player.platformDropTimer = 0;
  player.onWireframe = false;
  player.actionState = { tag: 'settled', settledX: spawnX, settledY: spawnY };
  player.selectedShotTier = 1;
  player.armedShotTier = null;
  player.armedDisrupted = false;
  // tankId persists across rounds (chosen at tank select)
  // terraformerMode persists across rounds (chosen at tank select)
  // Re-derive color from tank identity (tankId persists, color must match)
  const skinOverride = getSkinForTank(player.tankId);
  player.color = skinOverride.color ?? lookupTank(player.tankId).visual.bodyColor;
  player.selectedWeapon = null;
  player.pityCounter = 0;
  // Vortex portal interaction
  player.portalImmunityFrames = 0;
  player.collapseGrabbed = false;
  player.collapseFrame = 0;
  player.collapseTargetX = 0;
  player.collapseTargetY = 0;
  player.portalJumpUsed = false;
  player.portalJumpFrame = -1;
  player.portalJumpTargetX = 0;
  player.portalJumpTargetY = 0;
  // Dash immunity
  player.dashImmune = 0;
  // Cupid mark
  player.cupidMarkTurns = 0;
  player.cupidMarkedBy = -1;
  // isAI, aiDifficulty, dormant persist across rounds
  // Stock system — reset per round
  player.stocks = STOCKS.DEFAULT_COUNT;
  player.respawnTimer = 0;
  player.respawnInvulnFrames = 0;
}
